import { useEffect, useRef } from 'react';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { useAppSelector } from 'store/hook';
import DispatchMonitorAPI from 'api/dispatch-monitor.api';

const CAR_PUBSUB_CHANNEL = 'control-room:car:pubsub';

/**
 * Opens an SSE connection to /api/dispatch-monitor/live-dispatch-sse and
 * invokes `onLatestTimestamp` with the device id and `latest_ts` for every
 * message on the `control-room:car:pubsub` channel — the same live sensor
 * feed the Control Room map consumes, filtered down to just the field this
 * hook's consumers care about (when a device last reported any sensor data).
 *
 * Modeled on useLiveGpsSSE / useControlRoomSSE — same connect/parse/cleanup
 * shape, but unwraps the extra {channel, message} envelope this stream sends
 * instead of flat per-field messages.
 */
export function useDeviceLastSensorSSE(
  onLatestTimestamp: (deviceId: string, latestTs: number) => void
) {
  const accessToken = useAppSelector((s) => s.authReducer.accessToken);
  const customerId = useAppSelector((s) => s.persistedReducer.customer.id);
  const dispatchMonitorApi = useRef(new DispatchMonitorAPI()).current;
  const esRef = useRef<InstanceType<typeof EventSourcePolyfill> | null>(null);
  const onLatestTimestampRef = useRef(onLatestTimestamp);
  onLatestTimestampRef.current = onLatestTimestamp;

  useEffect(() => {
    if (!accessToken || !customerId) return;

    const url = dispatchMonitorApi.liveDispatchSSEUrl(customerId);

    const es = new EventSourcePolyfill(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    esRef.current = es;

    es.onmessage = function (e: any) {
      try {
        const outer = JSON.parse(e.data as string);
        if (outer?.channel !== CAR_PUBSUB_CHANNEL) return;

        const msg =
          typeof outer.message === 'string'
            ? JSON.parse(outer.message)
            : outer.message;

        const deviceId = msg?.device;
        const latestTs = Number(msg?.latest_ts);
        if (!deviceId || !latestTs) return;

        onLatestTimestampRef.current(deviceId, latestTs);
      } catch (err) {
        console.error('[device-last-sensor-sse] parse error:', err);
      }
    };

    es.onerror = () => {
      console.warn('[device-last-sensor-sse] connection error — reconnecting');
    };

    return () => {
      es.close();
      esRef.current = null;
    };
  }, [accessToken, customerId, dispatchMonitorApi]);
}
